import React, { createContext, useContext, useMemo, useState } from "react";
import {
  usePhotoUpload,
  UsePhotoUploadReturn,
} from "../../../../../generic/hooks/usePhotoUpload";
import { ICustomerItem } from "../../../../../modules/API/Drivers/models/kavData";
import useDeliveryConfirm, {
  IUseDeliveryConfirm,
} from "./hooks/useDeliveryConfirm";
import useDriversData, { IUseDriversData } from "./hooks/useDriversData";
import useDriversPrint, { IUseDriversPrint } from "./hooks/useDriversPrint";
import useKavActions, { IUseKavActions } from "./hooks/useKavActions";
import usePackageOrder, { IUsePackageOrder } from "./hooks/usePackageOrder";
import usePackageStatistic, {
  IUsePackageStatistic,
} from "./hooks/usePackageStatistic";
import useTeudotProblems, {
  IUseTeudotProblems,
} from "./hooks/useTeudotProblems";

export interface IDriversContextValue
  extends IUseDriversData,
    IUseDriversPrint,
    IUseKavActions,
    IUseDeliveryConfirm,
    IUsePackageOrder,
    IUseTeudotProblems,
    IUsePackageStatistic,
    UsePhotoUploadReturn {
  /** Клиент, выбранный последним действием — его читают и аризот, и подтверждение доставки. */
  customerData: ICustomerItem | undefined;
  setCustomerData: (customer: ICustomerItem) => void;
}

const DriversContext = createContext<IDriversContextValue | null>(null);

export const DriversProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [customerData, setCustomerData] = useState<ICustomerItem>();

  const driversData = useDriversData();
  const { data, setData, date, isOffice, defaultStatuses } = driversData;

  const print = useDriversPrint(date);
  const kavActions = useKavActions({ setData, date });
  const deliveryConfirm = useDeliveryConfirm({
    setData,
    date,
    customerData,
    setCustomerData,
    defaultStatuses,
    listTeudotActions: driversData.listTeudotActions,
  });
  const packageOrder = usePackageOrder({
    data,
    setData,
    date,
    customerData,
    setCustomerData,
    isOffice,
    listPacakgesActions: driversData.listPacakgesActions,
  });
  const teudotProblems = useTeudotProblems({ setData });
  const packageStatistic = usePackageStatistic({ date, isOffice });
  const photoUpload = usePhotoUpload();

  const value = useMemo<IDriversContextValue>(
    () => ({
      ...driversData,
      ...print,
      ...kavActions,
      ...deliveryConfirm,
      ...packageOrder,
      ...teudotProblems,
      ...packageStatistic,
      ...photoUpload,
      customerData,
      setCustomerData,
    }),
    [
      driversData,
      print,
      kavActions,
      deliveryConfirm,
      packageOrder,
      teudotProblems,
      packageStatistic,
      photoUpload,
      customerData,
    ]
  );

  return (
    <DriversContext.Provider value={value}>{children}</DriversContext.Provider>
  );
};

export const useDrivers = (): IDriversContextValue => {
  const context = useContext(DriversContext);
  if (!context) {
    throw new Error("useDrivers must be used within DriversProvider");
  }
  return context;
};

export default DriversContext;
